import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Alert, Container } from 'react-bootstrap'
import { ArrowLeft, GitCompareArrows } from 'lucide-react'
import ProductComplianceScopeEditor from '../features/products/ProductComplianceScopeEditor'
import { fetchProducts } from '../services/api'
import { useAuth } from '../context/AuthContext'

export default function ProductComplianceScopePage() {
  const { productId } = useParams<{ productId: string }>()
  const { user } = useAuth()
  const userRole = user?.role?.toLowerCase() ?? ''
  const canEditScope =
    userRole === 'author' || userRole === 'publisher' || userRole === 'masteradmin'

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => fetchProducts(),
  })

  const product = useMemo(
    () => (productId ? products.find((p) => p.id === productId) : undefined),
    [products, productId],
  )

  if (!productId) {
    return (
      <Container className="py-4">
        <Alert variant="warning">Missing product id.</Alert>
        <Link to="/products">Back to products</Link>
      </Container>
    )
  }

  return (
    <Container className="py-4">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Link className="btn btn-outline-secondary btn-sm" to="/products">
          <ArrowLeft size={16} className="me-1" aria-hidden />
          Product library
        </Link>
        <Link className="btn btn-outline-primary btn-sm" to={`/products/${productId}/compare`}>
          <GitCompareArrows size={16} className="me-1" aria-hidden />
          Compare
        </Link>
      </div>

      <div className="text-muted small mb-2">
        <Link to="/products">Products</Link>
        <span className="mx-1">/</span>
        <span className="text-body">{isLoading ? '…' : product ? `${product.name} (${product.code})` : productId}</span>
      </div>

      <h1 className="h4 mb-1">Comparison scope</h1>
      <p className="text-muted small mb-4">
        Review and configure which canonical attributes are monitored for compliance extraction and value comparison for this product.
      </p>

      {!product && !isLoading && (
        <Alert variant="warning" className="mb-3">
          This product was not found in the loaded register (filters may exclude it). You can still adjust scope if the id is valid.
        </Alert>
      )}

      {!canEditScope && (
        <Alert variant="info" className="small py-2 mb-3">
          Your role can view this page but cannot save changes.
        </Alert>
      )}

      <ProductComplianceScopeEditor productId={productId} canEdit={canEditScope} title="Monitored attributes" />
    </Container>
  )
}
